/**
 * Design preview for the badge medallions (`design/badges`).
 *
 * All twenty at sheet size with labels, then the sizes they actually render at in the app, then the
 * unearned state the full panel needs. Same standing as `design/kombi` and `share/preview`: no loader,
 * no API, absent from the nav, not a product surface (PC-F14).
 *
 * The group order and glosses are the masterlist's own (migration 0025 / REQUIREMENTS_PROFILE §5.2), so
 * this page can be read against the requirements rather than against itself.
 */
import { BadgeIcon } from './BadgeIcon';

const GROUPS: { group: string; gloss: string; keys: [string, string][] }[] = [
  {
    group: 'Contributing',
    gloss: 'What you share',
    keys: [
      ['first_share', 'First Share'],
      ['toolmaker', 'Toolmaker'],
      ['workshop', 'Workshop'],
      ['first_reuse', 'First Reuse'],
      ['useful', 'Useful'],
      ['relied_on', 'Relied On'],
      ['indispensable', 'Indispensable'],
      ['in_the_five', 'In The Five'],
    ],
  },
  {
    group: 'Reusing',
    gloss: 'What you adopt',
    keys: [
      ['first_adopter', 'First Adopter'],
      ['reuser', 'Reuser'],
      ['serial_reuser', 'Serial Reuser'],
    ],
  },
  {
    group: 'Helping',
    gloss: 'What you give back',
    keys: [
      ['helpful_voice', 'Helpful Voice'],
      ['good_citizen', 'Good Citizen'],
      ['checked_it_works', 'Checked It Works'],
      ['answered', 'Answered'],
    ],
  },
  {
    group: 'Learning',
    gloss: 'What you build up',
    keys: [
      ['welcome', 'Welcome'],
      ['safe_hands', 'Safe Hands'],
      ['curious', 'Curious'],
      ['levelled_up', 'Levelled Up'],
    ],
  },
  {
    group: 'Recognition',
    gloss: 'Awarded by a person',
    keys: [['above_and_beyond', 'Above and Beyond']],
  },
];

const H2: React.CSSProperties = { fontSize: '0.95rem', fontWeight: 500, margin: '0 0 2px' };
const GLOSS: React.CSSProperties = {
  fontSize: '0.78rem',
  color: 'var(--accent)',
  margin: '0 0 10px',
};
const NOTE: React.CSSProperties = { fontSize: '0.78rem', opacity: 0.6, margin: '8px 0 0' };
const ROW: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 4 };

export function BadgePreview() {
  return (
    <section aria-labelledby="badge-preview-heading" style={{ display: 'grid', gap: 26 }}>
      <h1 id="badge-preview-heading" style={{ fontSize: '1.1rem' }}>
        Badges — 256&times;256, star rungs derived from the masterlist thresholds
      </h1>

      {GROUPS.map(({ group, gloss, keys }) => (
        <div key={group}>
          <h2 style={H2}>{group}</h2>
          <p style={GLOSS}>{gloss}</p>
          <div style={ROW}>
            {keys.map(([key, name]) => (
              <div key={key} style={{ width: 168 }}>
                <BadgeIcon badgeKey={key} label={name} />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div>
        <h2 style={H2}>The sizes the app actually uses</h2>
        <p style={GLOSS}>
          96 / 64 / 40 / 24 — with the title on the square, as the profile band now renders them
        </p>
        {[96, 64, 40, 24].map((px) => (
          <div key={px} style={{ ...ROW, alignItems: 'center', marginBottom: 6 }}>
            {(
              [
                ['first_share', 'First Share'],
                ['workshop', 'Workshop'],
                ['relied_on', 'Relied On'],
                ['indispensable', 'Indispensable'],
                ['above_and_beyond', 'Above and Beyond'],
              ] as [string, string][]
            ).map(([k, n]) => (
              <div key={k} style={{ width: px }}>
                <BadgeIcon badgeKey={k} label={n} />
              </div>
            ))}
          </div>
        ))}
        <p style={NOTE}>
          64px is the profile band&rsquo;s slot. The tier PLATE is what survives there — the title
          is a shape rather than a word until about 120px, which is the trade the square makes to
          carry all three signals.
        </p>
      </div>

      <div>
        <h2 style={H2}>Earned vs not yet earned</h2>
        <p style={GLOSS}>The full panel lists every badge, both ways</p>
        <div style={ROW}>
          {['toolmaker', 'in_the_five', 'curious', 'above_and_beyond'].map((k) => (
            <div key={k} style={{ width: 96 }}>
              <BadgeIcon badgeKey={k} />
            </div>
          ))}
          {['toolmaker', 'in_the_five', 'curious', 'above_and_beyond'].map((k) => (
            <div key={`${k}-locked`} style={{ width: 96 }}>
              <BadgeIcon badgeKey={k} earned={false} />
            </div>
          ))}
        </div>
        <p style={NOTE}>
          Unearned drains every colour onto the ink opacity ramp rather than fading with opacity, so
          the medallion keeps its edge against the page and the gold — which is the claim — goes
          entirely.
        </p>
      </div>

      <div>
        <h2 style={H2}>The rung ladder, side by side</h2>
        <p style={GLOSS}>1 to 5 — sand, pale teal, mid teal, light gold, gold</p>
        <div style={ROW}>
          {['first_share', 'useful', 'workshop', 'indispensable', 'above_and_beyond'].map((k) => (
            <div key={k} style={{ width: 120 }}>
              <BadgeIcon badgeKey={k} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
